import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Eye,
  EyeOff,
  Hammer,
  Home as HomeIcon,
  CheckCircle2,
} from "lucide-react";

export default function SignupPage() {
  const navigate = useNavigate();
  const { signUp, loading: authLoading } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("homeowner"); // 'installer' | 'homeowner'

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    // Password validation
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    // Signup user
    const { user, error: err } = await signUp(email, password, {
      full_name: name,
      role,
    });

    if (err) {
      setError(err.message || "Failed to create account");
    } else {
      setMessage("Account created! Please check your email to verify.");

      // ✅ Check for pending order
      const pendingOrder = localStorage.getItem("pending_order");

      if (pendingOrder) {
        // Redirect to make-payment page to continue order
        navigate("/confirm-installation", { replace: true });
      } else {
        // Normal role-based redirect
        const role = user?.user_metadata?.role;
        if (role === "installer") navigate("/installer", { replace: true });
        else navigate("/dashboard", { replace: true });
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white">
      {/* Top nav */}
      <div className="col-span-1 md:col-span-2">
        <div className="flex items-center justify-between px-6 md:px-10 py-4">
          {/* Brand */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2"
            data-testid="auth-topbrand"
          >
            <span className="inline-flex h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600" />
            <span className="font-extrabold text-slate-900 tracking-tight">
              RoofBidder
            </span>
          </button>
          <div className="flex items-center gap-6 text-sm">
            <a
              href="mailto:support@roofbidder.com"
              className="text-slate-600 hover:text-slate-900"
              data-testid="auth-contact-link"
            >
              Contact us
            </a>
            <Link
              to="/login"
              className="text-blue-600 font-semibold"
              data-testid="auth-login-link"
            >
              Log in
            </Link>
          </div>
        </div>
      </div>

      {/* Left brand/gradient panel */}
      <div className="hidden md:block bg-[linear-gradient(135deg,#eef2ff_0%,#f5f7ff_35%,#e9eefc_70%,#eef2ff_100%)]" />

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-6 md:px-12">
        <div className="w-full max-w-sm" data-testid="signup-page">
          <h1
            className="text-[32px] leading-tight font-extrabold tracking-tight text-slate-900 mb-2"
            data-testid="signup-title"
          >
            Create your RoofBidder account
          </h1>
          <p className="text-slate-600 text-sm mb-6">
            Choose how you want to join:
          </p>

          {/* Role selector */}
          <div
            className="grid grid-cols-2 gap-3 mb-6"
            data-testid="role-selector"
          >
            <button
              type="button"
              data-testid="role-homeowner"
              onClick={() => setRole("homeowner")}
              className={`rounded-xl border p-3 text-left transition ${
                role === "homeowner"
                  ? "border-blue-600 ring-2 ring-blue-200 bg-blue-50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <HomeIcon
                  className={`w-4 h-4 ${
                    role === "homeowner" ? "text-blue-600" : "text-slate-500"
                  }`}
                />
                <div className="text-sm font-semibold">Homeowner</div>
                {role === "homeowner" && (
                  <CheckCircle2 className="w-4 h-4 text-blue-600 ml-auto" />
                )}
              </div>
              <div className="text-xs text-slate-600">
                Request quotes and compare bids
              </div>
            </button>
            <button
              type="button"
              data-testid="role-installer"
              onClick={() => setRole("installer")}
              className={`rounded-xl border p-3 text-left transition ${
                role === "installer"
                  ? "border-indigo-600 ring-2 ring-indigo-200 bg-indigo-50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Hammer
                  className={`w-4 h-4 ${
                    role === "installer" ? "text-indigo-600" : "text-slate-500"
                  }`}
                />
                <div className="text-sm font-semibold">Installer</div>
                {role === "installer" && (
                  <CheckCircle2 className="w-4 h-4 text-indigo-600 ml-auto" />
                )}
              </div>
              <div className="text-xs text-slate-600">
                Bid on jobs and win more work
              </div>
            </button>
          </div>

          {error && (
            <div
              className="mb-5 bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-xl"
              data-testid="signup-error"
            >
              {error}
            </div>
          )}
          {message && (
            <div
              className="mb-5 bg-green-50 text-green-700 border border-green-200 px-4 py-3 rounded-xl"
              data-testid="signup-success"
            >
              {message}
            </div>
          )}

          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="signup-form"
          >
            <div>
              <label
                className="block text-sm font-medium text-slate-700 mb-1"
                htmlFor="name"
              >
                Full name
              </label>
              <input
                id="name"
                data-testid="signup-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                className="w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium text-slate-700 mb-1"
                htmlFor="email"
              >
                Email address
              </label>
              <input
                id="email"
                data-testid="signup-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                className="w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-3 text-[15px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium text-slate-700 mb-1"
                htmlFor="password"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="signup-password-input"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-3 pr-10 text-[15px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-slate-500"
                  aria-label="Toggle password visibility"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label
                className="block text-sm font-medium text-slate-700 mb-1"
                htmlFor="confirm"
              >
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="confirm"
                  data-testid="signup-confirm-input"
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full h-12 rounded-xl border border-slate-200 bg-slate-50 px-3 pr-10 text-[15px] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute inset-y-0 right-0 px-3 text-slate-500"
                  aria-label="Toggle confirm visibility"
                  data-testid="toggle-confirm-visibility"
                >
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              data-testid="signup-submit-button"
              disabled={loading || authLoading}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold disabled:opacity-50 shadow-sm"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-slate-600 text-sm">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-blue-600 font-semibold"
              data-testid="goto-login-link"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
